/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Task 컨트롤러 클래스를 정의한다.
 *
 * @file /src/components/taskbar/elements/Task.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 18.
 */
import { PanelProvider } from '@base/Viewport';
import { Taskbar, TaskProvider } from '@components/taskbar/Taskbar';
import { Panel } from '@components/panel/Panel';
import { Window } from '@components/window/Window';
import { Menu } from '@components/menu/Menu';
import { Control } from '@elements/control/Control';
import { Dom } from '@utils/Dom';
import { Scroller } from '@utils/Scroller';
import { Language } from '@utils/Language';
import { Exceptions } from '@utils/Exceptions';

export class Task extends Control {
    /**
     * 엘리먼트 타입
     */
    type: string = 'control';

    /**
     * 작업고유값
     */
    id: string = null;

    /**
     * 패널프로바이더
     */
    provider: PanelProvider | string = null;

    /**
     * 작업 항목
     */
    task: Panel | Window = null;

    /**
     * 작업 URL
     */
    url: string | null = null;

    /**
     * 고정 여부
     */
    fixed: boolean = false;

    /**
     * 메뉴
     */
    menu: Menu = null;

    /**
     * Task 작업 엘리먼트를 정의한다.
     *
     * @param task - 작업
     * @param url - 기본 URL
     * @param fixed - 고정 여부
     * @param isActive - 활성화 여부
     */
    constructor(task: PanelProvider | TaskProvider | Panel | Window) {
        super();

        if (task instanceof Panel || task instanceof Window) {
            this.icon = task.getTitle()?.icon ?? 'mi mi-layers';
            this.text = task.getTitle()?.text ?? 'Unknown';
            this.provider = null;

            if (task instanceof Panel) {
                this.task = task;
            }

            if (task instanceof Window) {
                this.task = task;
                this.activeOnClick = true;

                task.addEvent('show', () => {
                    this.setActive(true);
                });

                task.addEvent('hide', () => {
                    this.setActive(false);
                });

                task.addEvent('minimize', () => {
                    this.setActive(false);
                });

                task.addEvent('restore', (_, beforeStatus) => {
                    if (beforeStatus == 'minimized') {
                        this.setActive(true);
                    }
                });

                task.addEvent('close', () => {
                    this.remove();
                });

                /**
                @todo Window 가 추가될때, Window 를 추가한 함수명과 그 함수가 정의되어 있는 JS 파일, 그리고 그 함수에 전달되는 매개변수가 Window 객체 어딘가에 저장되어 있다면, task 의 provider 를 생성해주고, 그로 인해 윈도우도 taskbar 의 restoreTasks 를 통해 페이지 새로고침시 복원할 수 있지 않을까?
                **/
            }
        } else if (typeof task == 'object') {
            if ('getPanel' in task) {
                if (typeof task.getPanel == 'function') {
                    this.icon = task.getIcon();
                    this.text = task.getTitle();
                    this.provider = task;

                    if ('setTask' in this.provider && typeof this.provider.setTask == 'function') {
                        this.provider.setTask(this);
                    }
                }
            } else {
                this.icon = task.icon;
                this.text = task.title;
                this.provider = task.base;
            }
        }

        this.movable = true;
        this.closable = true;

        this.$getElement().setData('task', this);
    }

    /**
     * 작업표시줄 컴포넌트를 가져온다.
     *
     * @returns 작업표시줄
     */
    getComponent(): Taskbar {
        return this.component as Taskbar;
    }

    /**
     * 작업 고유값을 가져온다.
     *
     * @returns 작업 고유값
     */
    getId(): string | null {
        return this.id;
    }

    /**
     * 컨테이너 DOM을 가져온다 (드래그 앤 드롭용)
     */
    getContainer(): Dom {
        return this.getComponent().$getTasks();
    }

    /**
     * 스크롤러를 가져온다 (드래그 앤 드롭용)
     */
    getScroller(): Scroller {
        return this.getComponent()?.getScroller() ?? null;
    }

    /**
     * 패널 프로바이더를 가져온다.
     *
     * @returns 패널 프로바이더
     */
    async getProvider(): Promise<PanelProvider | null> {
        if (this.provider === null) {
            return null;
        }

        if (typeof this.provider == 'string') {
            const provider = await this.getComponent().getViewport().panelLoader(this.provider);
            if (typeof provider == 'object' && 'getPanel' in provider && typeof provider.getPanel == 'function') {
                this.provider = provider;
                if ('setTask' in provider && typeof provider.setTask == 'function') {
                    this.provider.setTask(this);
                }
                return this.provider;
            }
        } else if (
            typeof this.provider == 'object' &&
            'getPanel' in this.provider &&
            typeof this.provider.getPanel == 'function'
        ) {
            return this.provider;
        }

        return null;
    }

    /**
     * 패널 프로바이더가 존재하는지 확인한다.
     *
     * @returns 패널 프로바이더 존재여부
     */
    hasProvider(): boolean {
        return this.provider !== null;
    }

    /**
     * 작업 컴포넌트를 가져온다.
     *
     * @returns 작업 컴포넌트
     */
    async getTask(): Promise<Panel | Window | null> {
        if (this.task !== null) {
            this.id = this.task.getId();
            return this.task;
        } else if (this.provider !== null) {
            if (typeof this.provider == 'string') {
                this.provider = await this.getProvider();
                this.task = (await this.provider?.getPanel()) ?? null;
                this.id = this.task?.getId() ?? null;
            } else {
                this.task = (await this.provider?.getPanel()) ?? null;
                this.id = this.task?.getId() ?? null;
            }

            this.task?.setTask(this);
            this.setTask(true);
            this.onUpdate();

            return this.task;
        } else {
            return null;
        }
    }

    /**
     * 작업이 존재하는지 여부를 설정한다. (실제로 작업을 불러오지는 않는다.)
     *
     * @param hasTask - 작업존재여부
     */
    setTask(hasTask: boolean): void {
        if (hasTask == true) {
            this.$getButton().addClass('task');
        } else {
            this.$getButton().removeClass('task');
        }
    }

    /**
     * 아이콘 상태에서, 작업이 존재하는지 확인한다.
     *
     * @param is_loaded - 작업이 실제로 로드되어 있는지 확인할지 여부
     * @returns 작업존재여부
     */
    hasTask(is_loaded: boolean = false): boolean {
        if (is_loaded == true) {
            return this.task !== null;
        }

        return this.$getButton().hasClass('task');
    }

    /**
     * 작업 패널을 가져온다.
     * 현재 작업이 윈도우인 경우 NULL 을 반환한다.
     *
     * @returns 작업 패널
     */
    async getPanel(): Promise<Panel | null> {
        const task = await this.getTask();
        return task instanceof Window ? null : task instanceof Panel ? task : null;
    }

    /**
     * 패널 프로바이더가 존재하지 않는 작업인 경우, Viewport 를 통해 작업이 활성화되었을 당시 URL 을 기록한다.
     *
     * @param url - 작업이 활성화되었을 떄의 URL
     */
    async setUrl(url: string): Promise<void> {
        if (this.provider === null) {
            this.url = url;
        } else if (typeof (await this.getProvider())?.setUrl == 'function') {
            /**
             * 패널 프로바이더의 기본 URL 의 하위 URL이 아닌 경우 URL 을 적용하지 않는다.
             */
            const provider = await this.getProvider();
            const baseUrl = provider.getUrl(true);

            if (url.indexOf(baseUrl) !== 0) {
                return;
            }

            (await this.getProvider()).setUrl(url, this);
        }

        /**
         * 현재 작업이 활성화되어 있다면, Viewport 의 주소도 함께 변경한다.
         */
        if (this.isActive() == true) {
            this.getComponent()
                .getViewport()
                .setUrl(url, await this.getText());
        }

        await this.onUpdate();
    }

    /**
     * 작업 URL 을 가져온다.
     *
     * @param is_base - 패널 프로바이더의 기본 URL 을 가져올지 여부
     * @returns URL
     */
    async getUrl(is_base: boolean = false): Promise<string> {
        if (this.provider === null) {
            return this.url;
        } else if (typeof this.provider == 'string') {
            return this.provider;
        } else {
            return (await this.getProvider()).getUrl(is_base);
        }
    }

    /**
     * 작업 메뉴를 가져온다.
     *
     * @returns 작업 메뉴
     */
    getMenu(): Menu {
        if (this.menu === null) {
            this.menu = new Menu({
                $target: this.$getButton(),
                menus: [
                    {
                        name: 'fix',
                        type: 'button',
                        icon: this.isFixed() == true ? 'mi mi-unpin' : 'mi mi-pin',
                        text: Language.get(
                            this.isFixed() == true ? 'components.taskbar.unfixed' : 'components.taskbar.fixed'
                        ),
                        hidden: this.hasProvider() == false,
                        handler: async () => {
                            await this.setFixed(this.isFixed() !== true);
                        },
                    },
                    {
                        name: 'close',
                        type: 'button',
                        icon: 'mi mi-close',
                        text: Language.get('components.taskbar.close'),
                        handler: async () => {
                            if (this.hasTask(true) == true) {
                                await this.task.remove();
                            } else {
                                await this.close();
                            }

                            return true;
                        },
                    },
                ],
                listeners: {
                    beforeShow: (menu) => {
                        this.$getButton().addClass('expand');

                        const fix = menu.getMenuByName('fix');
                        fix.setText(
                            Language.get(
                                this.isFixed() == true ? 'components.taskbar.unfixed' : 'components.taskbar.fixed'
                            )
                        );
                        fix.setIcon(this.isFixed() == true ? 'mi mi-unpin' : 'mi mi-pin');

                        const close = menu.getMenuByName('close');
                        close.setHidden(this.hasTask() == false);
                    },
                    hide: () => {
                        this.$getButton().removeClass('expand');
                    },
                },
            });
        }

        return this.menu;
    }

    /**
     * 작업 버튼 Dom을 가져온다.
     *
     * @returns 작업 버튼 Dom
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = super.$getButton();

            this.$icon = super.$getIcon();
            this.$icon.on('pointerdown', async (_, e) => {
                if (this.$button.hasClass('active') === false) return;

                if (this.getMenu().isShow() == true) {
                    this.getMenu().hide();
                } else {
                    this.$getButton().addClass('expand');
                    this.getMenu().show();
                }
            });

            this.$button.on('pointerenter', ($button) => {
                $button.addClass('over');
            });

            this.$button.on('contextmenu', (_, e) => {
                if (this.getMenu().isShow() == false) {
                    this.$getButton().addClass('expand');
                    this.getMenu().show();
                }

                e.preventDefault();
            });
        }

        return this.$button;
    }

    /**
     * 작업표시줄에 고정여부를 설정한다.
     *
     * @param fixed - 고정여부
     */
    async setFixed(fixed: boolean, is_store_tasks: boolean = true): Promise<void> {
        if (this.fixed === fixed) {
            return;
        }

        this.fixed = fixed;
        if (this.fixed == true) {
            this.$getButton().addClass('fixed');
        } else {
            if (this.hasTask() == true) {
                this.$getButton().removeClass('fixed');
            } else {
                await this.close();
            }
        }

        if (is_store_tasks == true) {
            await this.onUpdate();
        }
    }

    /**
     * 작업표시줄에 고정여부를 확인한다.
     *
     * @returns 고정여부
     */
    isFixed(): boolean {
        return this.fixed;
    }

    /**
     * 컨트롤을 활성화한다.
     */
    async active(): Promise<void> {
        this.setLoading(true);
        const task = await this.getTask();

        if (task instanceof Window) {
            if (task.isMinimized() == true) {
                await task.restore();
            } else if (task.isShow() == true) {
                task.toFront();
            }
            this.setLoading(false);
        } else if (task instanceof Panel) {
            const panel = await this.getPanel();
            const active = this.getComponent().getViewport().getActivePanel();

            if (panel === null || active?.id === panel?.id) {
                this.setLoading(false);
                return;
            }

            await this.getComponent()
                .getViewport()
                .active(this, await this.getUrl());

            this.setActive(true);
        } else {
            this.setLoading(false);
            Exceptions.warn('[Taskbar]', 'NOT_FOUND_TASK');
        }

        this.onUpdate();
    }

    /**
     * 작업을 닫는다.
     */
    async close(): Promise<void> {
        if (this.isFixed() == true) {
            this.task = null;
            this.setTask(false);
        } else {
            await this.remove();
        }

        await this.onUpdate();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (this.menu !== null) {
            this.menu.hide();
            this.menu.remove();
            this.menu = null;
        }

        this.task = null;
        this.provider = null;

        await super.remove(removeDom);
    }

    /**
     * 작업이 변경되었을 때 이벤트를 처리한다.
     */
    async onUpdate(): Promise<void> {
        this.getComponent()?.storeTasks();
        this.getScroller().updatePosition();
    }
}
